import React, { useEffect } from 'react'
import { Alert, Container, Form, Row, Col, Button } from 'react-bootstrap'
import PageTitle from '../../components/PageTitle'
import useAlert from '../../hooks/authentication/useAlert'
import useRegisterApp from '../../hooks/developer/useRegisterApp'
import { useFormik } from 'formik'
import { prepareValueForDb } from '../../utils/helpers'
import { AppRegisterSchema } from '../../utils/validation'
import { appConfig } from '../../assets/customizations/config'
import InputMask from 'react-input-mask'
import FhirLoader from '../../components/FhirLoader'
import useEnvironment from '../../hooks/location/useEnviroment'
import { Turnstile } from 'react-turnstile'
import useCsrfToken from '../../hooks/developer/useCsrfToken'

const DeveloperRegistration: React.FC = () => {
    const { alert, handleCloseAlert, showAlert } = useAlert({
        show: false,
        variant: 'info',
        message: '',
    })



    const { data: environmentData, isSuccess: isEnvironmentSuccess } = useEnvironment()
    const { data: csrfData } = useCsrfToken()

    const registerApp = useRegisterApp(csrfData?.csrfToken || '')

    useEffect(() => {
        if (registerApp.isSuccess) {
            showAlert('success', 'Thank you for registering your app. A confirmation email has been sent.')
        }

        if (registerApp.isError) {
            showAlert('danger', 'Unable to register your app: ')
        }
    }, [registerApp.isSuccess, registerApp.isError])

    const appForm = useFormik({
        initialValues: {
            readTerms: false,
            readFhirApiDocumentation: false,
            readSmartOnFhirDocumentation: false,
            readCarin: false,
            companyName: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            url: '',
            appName: '',
            appDescription: '',
            termsUrl: '',
            iosAppId: '',
            iosAppUrl: '',
            androidAppId: '',
            androidAppUrl: '',
            caqhDirectory: false,
            changeHealthcareDirectory: false,
            medEffectsDirectory: false,
            otherDir: false,
            otherDirectory: '',
            contactName: '',
            email: '',
            phone: '',
            directoryChecked: '',
            captcha: ''
        },
        onSubmit: (values) => {
            const newApp = {
                readTerms: values.readTerms,
                readFhirApiDocumentation: values.readFhirApiDocumentation,
                readSmartOnFhirDocumentation: values.readSmartOnFhirDocumentation,
                readCarin: values.readCarin,
                companyName: values.companyName,
                address1: values.address1,
                address2: prepareValueForDb(values.address2),
                city: values.city,
                state: values.state,
                zip: values.zip,
                url: values.url,
                appName: values.appName,
                appDescription: values.appDescription,
                termsUrl: values.termsUrl,
                iosAppId: values.iosAppId,
                iosAppUrl: values.iosAppUrl,
                androidAppId: values.androidAppId,
                androidAppUrl: values.androidAppUrl,
                caqhDirectory: values.caqhDirectory,
                changeHealthcareDirectory: values.changeHealthcareDirectory,
                medEffectsDirectory: values.medEffectsDirectory,
                otherDirectory: prepareValueForDb(values.otherDirectory),
                contactName: values.contactName,
                email: values.email,
                phone: values.phone,
                captcha: values.captcha
            }
            registerApp.mutate(newApp)
        },
        enableReinitialize: true,
        validationSchema: AppRegisterSchema
    })

    return (
        <section className='form'>
            <Container>
                <PageTitle displayName='Developer Registration' />
                <h1 className='headerText h2'>Developer Registration</h1>
                <Alert
                    variant={alert.variant}
                    show={alert.show}
                    onClose={handleCloseAlert}
                    dismissible
                >
                    {alert.message}
                    {
                        registerApp.isSuccess && (
                            <Alert.Link href='/developers' className='ml-3'>Go Back</Alert.Link>
                        )
                    }
                    {
                        registerApp.isError && (
                            <div>{registerApp.error?.response?.text}</div>
                        )
                    }
                </Alert>
                {
                    !registerApp.isSuccess && (
                        <div>
                            <Form noValidate onSubmit={appForm.handleSubmit} className='formContainer'>
                                <fieldset>
                                    <legend className="h3">Attestations</legend>
                                    <div>
                                        <Form.Check
                                            id='readTerms'
                                            name='readTerms'
                                            type='checkbox'
                                            label={
                                                <span>
                                                    {appConfig.terms_and_conditions_label}
                                                    <a
                                                        href={appConfig.terms_and_conditions_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Terms and Conditions
                                                    </a>
                                                </span>
                                            }
                                            checked={appForm.values.readTerms}
                                            onChange={appForm.handleChange}
                                            onBlur={appForm.handleBlur}
                                            isInvalid={!!appForm.errors.readTerms && appForm.touched.readTerms}
                                        />
                                        {!!appForm.errors.readTerms && appForm.touched.readTerms && (
                                            <Form.Text className='text-danger'>
                                                {appForm.errors.readTerms}
                                            </Form.Text>
                                        )}

                                        <Form.Check
                                            id='readFhirApiDocumentation'
                                            name='readFhirApiDocumentation'
                                            type='checkbox'
                                            label={
                                                <span>
                                                    {appConfig.fhir_api_label}
                                                    <a
                                                        href="https://build.fhir.org/documentation.html/"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        FHIR API Documentation
                                                    </a>
                                                </span>
                                            }
                                            checked={appForm.values.readFhirApiDocumentation}
                                            onChange={appForm.handleChange}
                                            onBlur={appForm.handleBlur}
                                            isInvalid={!!appForm.errors.readFhirApiDocumentation && appForm.touched.readFhirApiDocumentation}
                                        />
                                        {!!appForm.errors.readFhirApiDocumentation && appForm.touched.readFhirApiDocumentation && (
                                            <Form.Text className='text-danger'>
                                                {appForm.errors.readFhirApiDocumentation}
                                            </Form.Text>
                                        )}

                                        <Form.Check
                                            id='readSmartOnFhirDocumentation'
                                            name='readSmartOnFhirDocumentation'
                                            type='checkbox'
                                            label={
                                                <span>
                                                    {appConfig.smart_fhir_documentation_label}
                                                    <a
                                                        href="https://docs.smarthealthit.org/"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        SMART on FHIR Documentation
                                                    </a>
                                                </span>
                                            }
                                            checked={appForm.values.readSmartOnFhirDocumentation}
                                            onChange={appForm.handleChange}
                                            onBlur={appForm.handleBlur}
                                            isInvalid={!!appForm.errors.readSmartOnFhirDocumentation && appForm.touched.readSmartOnFhirDocumentation}
                                        />
                                        {!!appForm.errors.readSmartOnFhirDocumentation && appForm.touched.readSmartOnFhirDocumentation && (
                                            <Form.Text className='text-danger'>
                                                {appForm.errors.readSmartOnFhirDocumentation}
                                            </Form.Text>
                                        )}

                                        <Form.Check
                                            id='readCarin'
                                            name='readCarin'
                                            type='checkbox'
                                            label={
                                                <span>
                                                    {appConfig.carin_code_label}
                                                    <a
                                                        href='https://www.carinalliance.com/code-of-conduct'
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    >
                                                        Carin Code of Conduct
                                                    </a>
                                                </span>
                                            }
                                            checked={appForm.values.readCarin}
                                            onChange={appForm.handleChange}
                                            onBlur={appForm.handleBlur}
                                            isInvalid={!!appForm.errors.readCarin && appForm.touched.readCarin}
                                        />
                                        {!!appForm.errors.readCarin && appForm.touched.readCarin && (
                                            <Form.Text className='text-danger'>
                                                {appForm.errors.readCarin}
                                            </Form.Text>
                                        )}
                                    </div>
                                </fieldset>

                                <h2 className='mt-2 h3'>Company Information</h2>
                                <Form.Group>
                                    <Form.Label htmlFor='companyName'>Company Name:</Form.Label>
                                    <Form.Control
                                        id='companyName'
                                        name='companyName'
                                        type='text'
                                        value={appForm.values.companyName}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.companyName && appForm.touched.companyName}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.companyName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='address1'>Address:</Form.Label>
                                    <Form.Control
                                        id='address1'
                                        name='address1'
                                        type='text'
                                        value={appForm.values.address1}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.address1 && appForm.touched.address1}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.address1}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='address2'>Address 2:</Form.Label>
                                    <Form.Control
                                        id='address2'
                                        name='address2'
                                        type='text'
                                        value={appForm.values.address2}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                    />
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='city'>City:</Form.Label>
                                    <Form.Control
                                        id='city'
                                        name='city'
                                        type='text'
                                        value={appForm.values.city}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.city && appForm.touched.city}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.city}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='state'>State/Province:</Form.Label>
                                    <Form.Control
                                        id='state'
                                        name='state'
                                        as='select'
                                        value={appForm.values.state}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.state && appForm.touched.state}
                                    >
                                        <option value="" disabled>Choose State/Province</option>
                                        <option value="Alabama">AL</option>
                                        <option value="Alaska">AK</option>
                                        <option value="American Samoa">AS</option>
                                        <option value="Arizona">AZ</option>
                                        <option value="Arkansas">AR</option>
                                        <option value="California">CA</option>
                                        <option value="Colorado">CO</option>
                                        <option value="Connecticut">CT</option>
                                        <option value="Delaware">DE</option>
                                        <option value="District Of Columbia">DC</option>
                                        <option value="Federated States Of Micronesia">FM</option>
                                        <option value="Florida">FL</option>
                                        <option value="Georgia">GA</option>
                                        <option value="Guam">GU</option>
                                        <option value="Hawaii">HI</option>
                                        <option value="Idaho">ID</option>
                                        <option value="Illinois">IL</option>
                                        <option value="Indiana">IN</option>
                                        <option value="Iowa">IA</option>
                                        <option value="Kansas">KS</option>
                                        <option value="Kentucky">KY</option>
                                        <option value="Louisiana">LA</option>
                                        <option value="Maine">ME</option>
                                        <option value="Marshall Islands">MH</option>
                                        <option value="Maryland">MD</option>
                                        <option value="Massachusetts">MA</option>
                                        <option value="Michigan">MI</option>
                                        <option value="Minnesota">MN</option>
                                        <option value="Mississippi">MS</option>
                                        <option value="Missouri">MO</option>
                                        <option value="Montana">MT</option>
                                        <option value="Nebraska">NE</option>
                                        <option value="Nevada">NV</option>
                                        <option value="New Hampshire">NH</option>
                                        <option value="New Jersey">NJ</option>
                                        <option value="New Mexico">NM</option>
                                        <option value="New York">NY</option>
                                        <option value="North Carolina">NC</option>
                                        <option value="North Dakota">ND</option>
                                        <option value="Northern Mariana Islands">MP</option>
                                        <option value="Ohio">OH</option>
                                        <option value="Oklahoma">OK</option>
                                        <option value="Oregon">OR</option>
                                        <option value="Palau">PW</option>
                                        <option value="Pennsylvania">PA</option>
                                        <option value="Puerto Rico">PR</option>
                                        <option value="Rhode Island">RI</option>
                                        <option value="South Carolina">SC</option>
                                        <option value="South Dakota">SD</option>
                                        <option value="Tennessee">TN</option>
                                        <option value="Texas">TX</option>
                                        <option value="Utah">UT</option>
                                        <option value="Vermont">VT</option>
                                        <option value="Virgin Islands">VI</option>
                                        <option value="Virginia">VA</option>
                                        <option value="Washington">WA</option>
                                        <option value="West Virginia">WV</option>
                                        <option value="Wisconsin">WI</option>
                                        <option value="Wyoming">WY</option>
                                    </Form.Control>
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.state}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='zip'>Zip/Postal Code:</Form.Label>
                                    <Form.Control
                                        id='zip'
                                        name='zip'
                                        type='text'
                                        value={appForm.values.zip}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.zip && appForm.touched.zip}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.zip}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='url'>Company URL:</Form.Label>
                                    <Form.Control
                                        id='url'
                                        name='url'
                                        type='text'
                                        value={appForm.values.url}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.url && appForm.touched.url}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.url}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <h2 className='mt-2 h3'>App Information</h2>
                                <Form.Group>
                                    <Form.Label htmlFor='appName'>App Name:</Form.Label>
                                    <Form.Control
                                        id='appName'
                                        name='appName'
                                        type='text'
                                        value={appForm.values.appName}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.appName && appForm.touched.appName}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.appName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label htmlFor='appDescription'>App Description:</Form.Label>
                                    <Form.Control
                                        id='appDescription'
                                        name='appDescription'
                                        as='textarea'
                                        rows={3}
                                        value={appForm.values.appDescription}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.appDescription && appForm.touched.appDescription}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.appDescription}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='termsUrl'>Terms and Conditions URL:</Form.Label>
                                    <Form.Control
                                        id='termsUrl'
                                        name='termsUrl'
                                        type='text'
                                        value={appForm.values.termsUrl}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.termsUrl && appForm.touched.termsUrl}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.termsUrl}
                                    </Form.Control.Feedback>
                                </Form.Group>
        

                                <h2 className='mt-2 h3'>App Contact</h2>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='contactName'>Your Name:</Form.Label>
                                    <Form.Control
                                        id='contactName'
                                        name='contactName'
                                        type='text'
                                        value={appForm.values.contactName}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.contactName && appForm.touched.contactName}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.contactName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='phone'>Your Phone:</Form.Label>
                                    <InputMask
                                        mask="(999)-999-9999"
                                        value={appForm.values.phone}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                    >
                                        {(inputProps) => (
                                            <Form.Control
                                                id='phone'
                                                {...inputProps}
                                                name="phone"
                                                isInvalid={!!appForm.errors.phone && appForm.touched.phone}
                                            />
                                        )}
                                    </InputMask>
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.phone}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Form.Label htmlFor='email'>Your Email:</Form.Label>
                                    <Form.Control
                                        id='email'
                                        name='email'
                                        type='text'
                                        value={appForm.values.email}
                                        onChange={appForm.handleChange}
                                        onBlur={appForm.handleBlur}
                                        isInvalid={!!appForm.errors.email && appForm.touched.email}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {appForm.errors.email}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    {
                                        isEnvironmentSuccess && (
                                            <Turnstile
                                                sitekey={environmentData?.turnstileSiteKey || ''}
                                                onSuccess={(value) => appForm.setFieldValue('captcha', value)}
                                                theme='light'
                                                execution="render"
                                                aria-label="CAPTCHA: Please verify that you are not a robot"
                                            />
                                        )
                                    }
                                    {
                                        Boolean(!!appForm.errors.captcha && appForm.touched.captcha) && (
                                            <Form.Text className='text-danger'>
                                                {appForm.errors.captcha}
                                            </Form.Text>
                                        )
                                    }
                                </Form.Group>
                                <Form.Group className='mt-3'>
                                    <Col xs={12}>
                                        <Button
                                            variant='primary'
                                            type='submit'
                                            disabled={registerApp.isLoading || !csrfData?.csrfToken}
                                        >
                                            {
                                                registerApp.isLoading ? (
                                                    <FhirLoader small={true} />
                                                ) : 'Register'
                                            }
                                        </Button>
                                    </Col>
                                </Form.Group>
                            </Form>
                        </div>
                    )
                }
            </Container>
        </section>
    )
}

export default DeveloperRegistration